import { useCallback } from 'react';

import { notifications } from '@mantine/notifications';

import { vnextApiClient } from '@/services/vnext-api';
import type {
  ProfileDecision,
  ProfileReviewList,
  ProfileReviewSubmitResponse,
  SubjectProfile,
} from '@/types/profile-review';
import { useReviewQueue, type ReviewMutationFailure } from './useReviewQueue';

export interface UseProfileReviewsResult {
  data: ProfileReviewList | null;
  isLoading: boolean;
  isMutating: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  /** Record (or change) the verdict on one row. Updates the row in place. */
  decide: (
    rowId: string,
    decision: ProfileDecision,
    note?: string | null
  ) => Promise<SubjectProfile | null>;
  /** Add a profile the reviewer knows of; it arrives already confirmed. */
  add: (subjectId: string, url: string, note?: string | null) => Promise<SubjectProfile | null>;
  /** Reject every pending row still without a verdict; the queue is
   * replaced with the listing the server returns. */
  rejectRest: () => Promise<ProfileReviewList | null>;
  /** Release the confirmed profiles; the caller refreshes the report afterwards. */
  submit: () => Promise<ProfileReviewSubmitResponse | null>;
}

const LOAD_FALLBACK = 'Failed to load profile reviews';
const DECIDE_FAILURE: ReviewMutationFailure = {
  title: 'Decision not saved',
  fallback: 'Failed to record the decision',
};
const ADD_FAILURE: ReviewMutationFailure = {
  title: 'Profile not added',
  fallback: 'Failed to add the profile',
};
const REJECT_REST_FAILURE: ReviewMutationFailure = {
  title: 'Profiles not rejected',
  fallback: 'Failed to reject the undecided profiles',
};
const SUBMIT_FAILURE: ReviewMutationFailure = {
  title: 'Profiles not submitted',
  fallback: 'Failed to submit the profiles',
};

/** Module-level so the queue's `refetch` keeps its identity across renders. */
const loadProfileReviews = (reportId: string) => vnextApiClient.getProfileReviews(reportId);

/** Pending rows gate the report; pending rows without a verdict block submit. */
function recount(list: ProfileReviewList): ProfileReviewList {
  let pendingCount = 0;
  let undecidedCount = 0;
  for (const subject of list.subjects) {
    for (const profile of subject.profiles) {
      if (profile.status === 'pending') {
        pendingCount += 1;
        if (profile.decision === null) {
          undecidedCount += 1;
        }
      }
    }
  }
  return { ...list, pendingCount, undecidedCount };
}

function replaceProfile(list: ProfileReviewList, updated: SubjectProfile): ProfileReviewList {
  const subjects = list.subjects.map((s) => ({
    ...s,
    profiles: s.profiles.map((p) => (p.id === updated.id ? updated : p)),
  }));
  return recount({ ...list, subjects });
}

/** The subject group always exists: the backend rejects an unknown subject
 * with a 404 before a row is created. */
function appendProfile(list: ProfileReviewList, added: SubjectProfile): ProfileReviewList {
  const subjects = list.subjects.map((s) =>
    s.subjectId === added.subjectId ? { ...s, profiles: [...s.profiles, added] } : s
  );
  return recount({ ...list, subjects });
}

function submittedMessage(outcome: ProfileReviewSubmitResponse): string {
  const advanced = `Screening continues for ${outcome.advancedSubjectIds.length} subject(s).`;
  const late =
    outcome.lateSubjectIds.length > 0
      ? ` For ${outcome.lateSubjectIds.length} subject(s) the screening had already started; the confirmations are recorded only.`
      : '';
  return advanced + late;
}

/**
 * The profile-review queue for one report: the social media profiles per
 * subject awaiting a reviewer's verdict before the screening is ordered.
 * `null` reportId disables the fetch (same idiom as useMatchReviews).
 */
export function useProfileReviews(reportId: string | null | undefined): UseProfileReviewsResult {
  const { data, isLoading, isMutating, error, refetch, setData, mutate } = useReviewQueue(
    reportId,
    loadProfileReviews,
    LOAD_FALLBACK
  );

  const decide = useCallback(
    (rowId: string, decision: ProfileDecision, note?: string | null) =>
      mutate(
        DECIDE_FAILURE,
        (id) => vnextApiClient.decideProfileReview(id, rowId, decision, note),
        (updated) => setData((prev) => (prev ? replaceProfile(prev, updated) : prev))
      ),
    [mutate, setData]
  );

  const add = useCallback(
    (subjectId: string, url: string, note?: string | null) =>
      mutate(
        ADD_FAILURE,
        (id) => vnextApiClient.addProfileReview(id, subjectId, url, note),
        (added) => setData((prev) => (prev ? appendProfile(prev, added) : prev))
      ),
    [mutate, setData]
  );

  // The server recounts and returns the whole listing, which replaces the
  // queue as is (match review's `acceptAi` does the same).
  const rejectRest = useCallback(
    () =>
      mutate(
        REJECT_REST_FAILURE,
        (id) => vnextApiClient.rejectUndecidedProfileReviews(id),
        setData
      ),
    [mutate, setData]
  );

  const submit = useCallback(
    () =>
      mutate(
        SUBMIT_FAILURE,
        (id) => vnextApiClient.submitProfileReview(id),
        async (outcome) => {
          notifications.show({
            title: 'Profiles submitted',
            message: submittedMessage(outcome),
            color: 'green',
          });
          await refetch();
        }
      ),
    [mutate, refetch]
  );

  return { data, isLoading, isMutating, error, refetch, decide, add, rejectRest, submit };
}
